'use client';

import * as React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { cva, type VariantProps } from 'class-variance-authority';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { useNestedDismissableLayer } from '../overlayStack';

/** The shadcn/ui Select, on Radix `@radix-ui/react-select`.
 *
 *  This file is the shadcn component itself — its anatomy, its `data-slot` attributes and its prop
 *  surface are shadcn's, and the parts are exported individually so a call site composes them. What is
 *  ours is only the styling, which reads this app's design tokens instead of shadcn's default palette;
 *  that is the intended way to own a shadcn component, and it is why adopting shadcn does not repaint
 *  the product.
 *
 *  Behaviour is Radix's and is NOT reimplemented here: typeahead, roving focus, Home/End, Escape,
 *  focus restoration to the trigger, outside-press dismissal, scroll locking, `aria-*` wiring and
 *  collision-aware positioning. `components/ui/SelectMenu.tsx` is the app-shaped wrapper over these
 *  parts and is what most of the app imports. */

function Select({ ...props }: React.ComponentProps<typeof SelectPrimitive.Root>) {
  return <SelectPrimitive.Root data-slot="select" {...props} />;
}

/** `line` is this app's borderless variant, used where a picker sits inside a toolbar rather than in a
 *  form. It is an extra variant on the shadcn part, not a second component.
 *
 *  EXPORTED because this is the app's picker-trigger SURFACE, not just the Select's. `DateRangeFilter`
 *  opens a Popover and `ProjectFilterPills` opens a DropdownMenu, but all three sit in the same toolbar
 *  row and have to be the same control; both had grown a hand-copied duplicate of the `default` string
 *  below, which is how the three drifted apart one utility at a time. Composing this instead makes them
 *  identical by construction. A caller on a different Radix root passes its own display and focus ring
 *  through `cn`, which resolves the conflicts.
 *
 *  The open state's 3px halo reads `--color-ring` — the same token `focus-visible:ring-ring` reads — as the
 *  ordinary ring utility at one tenth of its strength. It used to be an arbitrary `box-shadow` carrying a
 *  hand-composed `color-mix`: the same paint in a spelling no skin could reach and no guard could see. As a
 *  ring it also composes with the focus ring instead of replacing it.
 *
 *  The trigger IS the field: it states the same 36px step and the same 44px coarse floor as
 *  `shadcn/input.tsx`, once, for `default` and `line` alike. A caller does not restate the floor — restating
 *  is how a toolbar's select fell 8px short of the search field beside it on a touch pointer. */
export const selectTriggerVariants = cva(
  'group flex h-9 w-full min-w-0 items-center gap-2 text-ui transition-[border-color,background-color,box-shadow] focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-40 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 pointer-coarse:min-h-[var(--touch-target)]',
  {
    variants: {
      variant: {
        default:
          'rounded-md border border-input bg-control px-3 text-foreground hover:border-border-strong hover:bg-accent data-[state=open]:border-primary/60 data-[state=open]:bg-primary/10 data-[state=open]:text-primary data-[state=open]:ring-3 data-[state=open]:ring-ring/10',
        line: 'border-b border-input bg-transparent px-1 text-foreground hover:border-border-strong data-[state=open]:border-primary data-[state=open]:text-primary',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

function SelectTrigger({
  className = '',
  variant = 'default',
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger> & VariantProps<typeof selectTriggerVariants>) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      data-variant={variant}
      className={cn(selectTriggerVariants({ variant }), className)}
      {...props}
    >
      {children}
      {/* Same glyph as the picker trigger's — `size={14}` at `opacity-60` (see RowPickerTrigger's
          shared selectTriggerVariants). A record can hold a Radix select and a row picker in adjacent rows,
          and a 13px chevron beside a 14px one reads as a rendering fault rather than as two controls.
          Both rotate it while open: each dropdown opens in place, so each trigger carries an open state. */}
      <SelectPrimitive.Icon asChild>
        <ChevronDown
          size={14}
          aria-hidden
          className="shrink-0 opacity-60 transition-transform group-data-[state=open]:rotate-180"
        />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

function SelectContent({
  className = '',
  children,
  position = 'popper',
  ref,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content>) {
  const layerRef = useNestedDismissableLayer(ref);
  return (
    // DELIBERATELY NOT PORTALED, unlike stock shadcn. This app isolates the background of an open
    // modal by marking every other child of <body> `inert` and `aria-hidden`
    // (`components/ui/overlayStack.ts`), and it traps focus inside the dialog. A portaled menu is a new
    // child of <body>, so it lands outside the dialog it visually belongs to: the focus trap pulls
    // focus back out of it, and the next overlay to open marks it inert. Rendering in place keeps the
    // menu inside whatever surface owns it, which is also exactly where it was before the port.
    // Re-running `shadcn add select` will put the Portal back — that is the one line to remove again.
    <SelectPrimitive.Content
      ref={layerRef}
      data-slot="select-content"
      position={position}
      // `overlay-layer-menu` is the app's menu z-index band, so a menu paints above the surface it
      // opens from.
      className={cn(
        'overlay-layer-menu max-h-96 w-max min-w-[var(--radix-select-trigger-width)] max-w-80 overflow-y-auto overflow-x-hidden',
        'rounded-xl border border-border bg-popover p-1.5 text-popover-foreground shadow-raised',
        'data-[state=open]:animate-fade-up',
        position === 'popper' && 'data-[side=bottom]:translate-y-2 data-[side=top]:-translate-y-2',
        className,
      )}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(position === 'popper' && 'min-w-full scroll-my-1')}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  );
}

/** The one look of a single-choice row: geometry, type and the current pick's primary wash, keyed to
 *  `data-state="checked"`. EXPORTED because it is the app's choice-row SURFACE, not just this Select's:
 *  the row picker's cmdk list (`RowPicker`) renders the same rows, marks its current pick with the same
 *  attribute and adds only its own highlight selector, so the two dropdowns read as one control. The
 *  highlight stays per primitive because Radix marks it `data-highlighted` and cmdk `data-selected`. */
export const choiceItemClass = cn(
  'relative flex w-full min-w-0 cursor-default select-none items-center gap-2 rounded-lg px-2.5 py-2 text-left text-ui outline-none transition-colors',
  'text-foreground data-[state=checked]:bg-primary/10 data-[state=checked]:text-primary',
);

/** The current pick's check, trailing the row in the primary ink. */
export const choiceCheckClass = 'ml-auto size-3.75 shrink-0 text-primary';

/** `icon` is a sibling of the item's text, never a child of it. `ItemText` renders one inline span whose
 *  content Radix also mirrors into the trigger as the selected value, so a `display:flex` glyph placed
 *  inside it becomes a block box in an inline parent and drops the label onto its own line. Passing the
 *  glyph separately keeps the row a flex row and keeps the mirrored trigger value pure text. */
function SelectItem({ className = '', children, icon, ...props }: React.ComponentProps<typeof SelectPrimitive.Item> & { icon?: React.ReactNode }) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        choiceItemClass,
        // `accent` is the shadcn token for exactly this — the interactive surface — and in this app it
        // resolves to a wash of the foreground rather than a step up the surface ramp. That matters:
        // a surface step is a highlight a design can silently erase, and both designs here do erase it
        // (studio-oled paints surface, elevated and overlay with one card fill; the built-in design
        // separates them by 6/255), which is how this row once highlighted invisibly. A skin may
        // collapse the surface ramp but may not lower text contrast, so the wash reads by construction.
        'data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground',
        'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        className,
      )}
      {...props}
    >
      {icon}
      <SelectPrimitive.ItemText className="min-w-0 flex-1 truncate">{children}</SelectPrimitive.ItemText>
      <SelectPrimitive.ItemIndicator asChild>
        <Check size={15} aria-hidden className={choiceCheckClass} />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  );
}

// The two scroll buttons stay private: `SelectContent` mounts them itself, and a caller composing this
// menu never places them by hand.
function SelectScrollUpButton({ className = '', ...props }: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
  return (
    <SelectPrimitive.ScrollUpButton
      data-slot="select-scroll-up-button"
      className={cn('flex cursor-default items-center justify-center py-1 text-muted-foreground', className)}
      {...props}
    >
      <ChevronUp size={13} aria-hidden />
    </SelectPrimitive.ScrollUpButton>
  );
}

function SelectScrollDownButton({
  className = '',
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
  return (
    <SelectPrimitive.ScrollDownButton
      data-slot="select-scroll-down-button"
      className={cn('flex cursor-default items-center justify-center py-1 text-muted-foreground', className)}
      {...props}
    >
      <ChevronDown size={13} aria-hidden />
    </SelectPrimitive.ScrollDownButton>
  );
}

export {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
};
